import React from 'react';
import { MenuProps } from 'antd';
import {
  DashboardOutlined,
  TagsOutlined,
  ShoppingOutlined,
  SettingOutlined,
  InboxOutlined,
  CalendarOutlined,
  AppstoreOutlined,
  CheckSquareOutlined,
  QuestionCircleOutlined,
  BarsOutlined,
  MobileOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { useEventContext } from '../../contexts/EventContext';
import { useQuery } from '@tanstack/react-query';
import { EventsService, setTenantContext } from '../../api/client';
import { getLocalizedString } from '../../utils/locale';
import { TenantContext, UserProfile } from '../../types';
import { useTranslation } from '../../hooks/useTranslation';

// Helper type: a single menu item from Ant Design MenuProps
type MenuItem = NonNullable<MenuProps['items']>[number];

interface EventMenuProps {
  tenant: TenantContext;
  user: UserProfile;
  selectedKeys: string[];
  openKeys: string[];
  onOpenChange: (openKeys: string[]) => void;
  onMenuClick: (path: string) => void;
}

export const EventMenu = ({
  tenant,
  user,
  selectedKeys: _selectedKeys,
  openKeys: _openKeys,
  onOpenChange: _onOpenChange,
  onMenuClick,
}: EventMenuProps): { items: MenuItem[] } => {
  const { getEventUrl, selectedEvent } = useEventContext();
  const { t } = useTranslation();

  // Fetch event details to get the proper name
  const { data: eventData } = useQuery({
    queryKey: ['event', tenant.organization_slug, selectedEvent],
    queryFn: async () => {
      if (!selectedEvent) return null;

      setTenantContext(tenant);

      // Use the events endpoint to get event details
      const eventsData = await EventsService.getOrganizersEvents(
        tenant.organization_slug,
        0,
        1,
        selectedEvent // search by slug
      );

      // Find the event with matching slug
      const event = eventsData.results?.find(e => e.slug === selectedEvent);
      return event || null;
    },
    enabled: !!selectedEvent && !!tenant.organization_slug,
  });

  // Don't render if no event is selected
  if (!selectedEvent) {
    return { items: [] };
  }

  // Simple role-based access check
  const hasRoleAccess = (key: string): boolean => {
    if (!user) return false;

    // Platform users can access everything
    if (user.user_type === 'platform_user') return true;

    // For org users, check based on roles
    if (user.user_type === 'org_user') {
      const userRoles = user.roles || user.organization_roles || [];

      // org_admin can access all event-related menus
      if (userRoles.includes('org_admin')) return true;

      // Other roles have limited access
      if (userRoles.includes('event_manager')) {
        return [
          'event-dashboard',
          'items',
          'categories',
          'questions',
          'quotas',
          'subevents',
          'groups',
          'orders',
          'checkinlists',
          'checkin-devices',
          'thirdparty-products',
          'thirdparty-inventory',
          'event-settings'
        ].includes(key);
      }

      if (userRoles.includes('staff')) {
        return ['event-dashboard', 'items', 'categories', 'questions', 'quotas', 'subevents', 'groups', 'orders', 'checkinlists'].includes(key);
      }
    }

    // Default: only allow dashboard
    return key === 'event-dashboard';
  };

  const createEventMenuItem = (key: string, label: string, icon: React.ReactNode, path: string, _feature?: string): MenuItem | null => {
    if (!hasRoleAccess(key)) {
      return null;
    }

    const menuItem: MenuItem = {
      key,
      icon,
      label,
      onClick: () => onMenuClick(path),
    };

    return menuItem;
  };

  const eventMenuItems: MenuItem[] = [
    createEventMenuItem(
      'event-dashboard',
      t('menu.event.eventDashboard'),
      <DashboardOutlined />,
      getEventUrl('')
    ),
    createEventMenuItem(
      'items',
      t('menu.event.products'),
      <TagsOutlined />,
      getEventUrl('items'),
      'events_enabled'
    ),
    createEventMenuItem(
      'categories',
      t('menu.event.categories'),
      <AppstoreOutlined />,
      getEventUrl('categories'),
      'events_enabled'
    ),
    createEventMenuItem(
      'questions',
      t('menu.event.questions'),
      <QuestionCircleOutlined />,
      getEventUrl('questions'),
      'events_enabled'
    ),
    createEventMenuItem(
      'quotas',
      t('menu.event.quotas'),
      <InboxOutlined />,
      getEventUrl('quotas'),
      'events_enabled'
    ),
    createEventMenuItem(
      'subevents',
      t('menu.event.subevents'),
      <CalendarOutlined />,
      getEventUrl('subevents'),
      'events_enabled'
    ),
    // "Phòng chiếu" (event_group — mantik-cinema-chain.md §5B) — CHỈ áp dụng cho event loại "cinema"
    // (event = RẠP, group = PHÒNG CHIẾU trong rạp đó); event "show"/"playground" không có khái niệm này.
    eventData?.event_type === 'cinema'
      ? createEventMenuItem(
          'groups',
          t('menu.event.groups'),
          <HomeOutlined />,
          getEventUrl('groups'),
          'events_enabled'
        )
      : null,
    createEventMenuItem(
      'orders',
      t('menu.event.orders'),
      <ShoppingOutlined />,
      getEventUrl('orders'),
      'orders_enabled'
    ),
    // createEventMenuItem(
    //   'thirdparty-inventory',
    //   t('menu.event.thirdpartyInventory'),
    //   <InboxOutlined />,
    //   getEventUrl('thirdparty/inventory')
    // ),
    createEventMenuItem(
      'checkinlists',
      t('menu.event.checkinLists'),
      <CheckSquareOutlined />,
      getEventUrl('checkinlists')
    ),
    createEventMenuItem(
      'checkin-devices',
      t('menu.event.checkinDevices'),
      <MobileOutlined />,
      getEventUrl('checkin-devices')
    ),
    createEventMenuItem(
      'event-settings',
      t('menu.event.settings'),
      <SettingOutlined />,
      getEventUrl('settings')
    ),
  ].filter((item): item is MenuItem => item !== null);

  // Get display name for the event
  const eventDisplayName = eventData
    ? getLocalizedString(eventData.name, selectedEvent)
    : selectedEvent;

  // Truncate long event names
  const truncatedName = eventDisplayName.length > 25
    ? `${eventDisplayName.substring(0, 25)}...`
    : eventDisplayName;

  // Return a divider and a group item
  return {
    items: [
      {
        type: 'divider',
      },
      {
        type: 'group',
        key: 'event-group',
        label: (
          <div
            style={{
              color: 'rgba(255,255,255,0.85)',
              fontSize: '12px',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '180px'
            }}
            title={t('menu.event.eventLabel', { truncatedName: eventDisplayName })}
          >
            {t('menu.event.eventLabel', { truncatedName })}
          </div>
        ),
        children: eventMenuItems,
      },
    ],
  };
}